// ModelPhotoImg — draws the picture useModelPhotos found, the one way for every screen.
//   photo.path (a files-bank photo)  → a thumbnail: asked for only when it is near the screen, six at a time, kept for the
//                                       session (files/data/thumbs.js) — never the full original
//   photo.src  (Sales master, dev card) → Sales' ProductImg (same-origin /img proxy, retries, placeholder)
// `fallback` is drawn when the picture cannot be shown. The parent owns the box; this only fills it.
import React, { useEffect, useRef, useState } from 'react';
// CSS order: the Acetate bank chunk loads acetateBank.module.css before ProductImg.module.css (see ModelPhoto.js in Orders Models).
import '../../portals/data/acetateBank/acetateBank.module.css';
import { ProductImg } from '../../portals/sales/components/ProductImg';
import { cachedThumb, SIZES, thumbFor } from '../../portals/files/data/thumbs';

const MARGIN = '240px';

function Thumb({ path, size, alt, className, fallback }) {
  const boxRef = useRef(null);
  const [url, setUrl] = useState(() => cachedThumb(path, size));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    const hit = cachedThumb(path, size);
    setUrl(hit);
    if (hit) return undefined;
    let alive = true;
    const ask = () => thumbFor(path, size).then((u) => { if (!alive) return; if (u) setUrl(u); else setFailed(true); });
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { ask(); return () => { alive = false; }; }
    const io = new IntersectionObserver((entries) => { if (!entries.some((e) => e.isIntersecting)) return; io.disconnect(); ask(); }, { rootMargin: MARGIN });
    io.observe(el);
    return () => { alive = false; io.disconnect(); };
  }, [path, size]);
  if (failed) return fallback || null;
  if (!url) return <span ref={boxRef} className={className} aria-busy="true" data-photo-wait />;
  return <img className={className} src={url} alt={alt} loading="lazy" decoding="async" />;
}

export default function ModelPhotoImg({ photo, alt = '', className, fallback, size = SIZES.card }) {
  if (!photo) return fallback || null;
  if (photo.path) return <Thumb path={photo.path} size={size} alt={alt} className={className} fallback={fallback} />;
  return <ProductImg src={photo.src} barcode={photo.barcode || undefined} alt={alt} className={className} fallback={fallback} />;
}
